/** @format */

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Button,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Table,
  TableRow,
  Skeleton,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Chip,
  Tooltip,
  Modal,
} from "@mui/material";
import SubSkills from "./Subskills";
import PopularityChart from "../PopularityChartV2";
import IndustrialUsageChart from "../IndustrialUsageChart";
import InterviewQuestions from "../Components/common/InterviewQuestions";
import DynamicContentRenderer from "../Components/common/DynamicContentRender";
import GoogleTrendsEmbed from "../Components/GoogleTrends";
import { RocketLaunch } from "@mui/icons-material";
import InterviewQuestionByCategory from "../Components/common/InterviewQuestionByCategory";
import TopSkillsInterviewQuestions from "../Components/common/TopSkillsInterviewQuestions";
import RolesAndResponsibilities from "./RolesAndResponsibilities";
import apis from "../utils/apis";
import { AddCircleOutline } from "@mui/icons-material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useSelector, useDispatch } from "react-redux";
import {
  setUserCountry,
  setSalaryRange,
  setUserExperienceRange,
  setSalaryRangeSkeleton,
  setUserState,
  setSingleSkeleton,
  setSingleCitySkeleton,
  setSalaryRangeByCities,
  setUserTopCities,
  setCitiesRangeSkeleton,
  setUserStateList,
} from "../../src/redux/action/userLocation";
import {
  fetchSalaryRangeFunction,
  fetchLocationData,
} from "../Components/common/FetchSalaryRange";
import {
  fetchSalaryRange,
  fetchSalaryRangeByCities,
  fetchCities,
  fetchTopCities,
  fetchCitiesInstance,
  fetchSalaryRangeByCitiesCountry,
  fetchStates,
} from "../Components/techrelevance.apis/fetchSalaryRange";
import "./GetInsights.css";
import JobSuggestionComponent from "./JobSuggestionComponent";
import AiToolsSection from "./AiToolsSection";
import SkillAndRoleDisruptionSection from "./SkillAndRoleDisruptionSection";
import SurvivalStrategySection from "./SurvivalStrategySection";
import { IncomeByExperienceSkeleton } from "../Components/common/IncomeByExperienceSkeleton";
import TechWorthRedirectAds from "./common/TechWorthRedirectAds";
import DropdownSkeleton from "./common/DropdownSkeleton";
import { lastIndexOf } from "lodash";
import LockIcon from "@mui/icons-material/Lock";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import usePaymentModule from "./common/PaymentModule";

// Function to render custom icons
const renderCustomIcon = (iconName, size = 24) => {
  const iconPath = `/assets/images/bookmark-icons/${iconName}.svg`;
  return (
    <img
      src={iconPath}
      alt={iconName}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
      }}
      onError={(e) => {
        // Fallback to Material-UI icon if custom icon fails to load
        console.warn(`Failed to load custom icon: ${iconName}`);
        e.target.style.display = "none";
      }}
    />
  );
};

// Common styles configuration
const STYLES = {
  colors: {
    primary: "#000088",
    secondary: "#262655",
    accent: "#4A3D9E",
    selected: "#F5F5FA",
    textPrimary: "#1A1A1A",
    textSecondary: "#888",
    textTertiary: "#999",
    border: "#D5D9EB",
    borderLight: "#E3E6F0",
    borderDashed: "#CFCFCF",
    background: "#F3FBFF",
    backgroundWhite: "#FFFFFF",
    backgroundHover: "#F9FBFF",
    buttonHover: "#FFF",
    skeleton: "#E3E6F0",
    skeletonWave:
      "linear-gradient(90deg, #E3E6F0 40%, #F9FBFF 60%, #E3E6F0 80%)",
    icon: "#B0B0B0",
  },
  typography: {
    fontFamily: "Poppins, sans-serif",
    title: {
      fontSize: { xs: "24px", lg: "28px" },
      fontWeight: 700,
    },
    subtitle: {
      fontSize: { xs: "18px", lg: "21px", xl: "24px" },
    },
    body: {
      fontSize: 15,
      fontWeight: 500,
    },
    tableHeader: {
      fontSize: { xs: "12px", lg: "22px" },
      fontWeight: 600,
    },
    tableCell: {
      fontSize: { xs: "12px", lg: "22px" },
      fontWeight: 400,
    },
    caption: {
      fontSize: 12,
      fontWeight: 500,
    },
  },
  spacing: {
    xs: 2,
    sm: 4,
    md: 3,
    lg: 5,
    xl: 5,
  },
  borderRadius: {
    card: "24px",
    button: "27px",
    select: "12px",
    swiperCard: "16px",
    small: "8px",
  },
  boxShadow: {
    card: "0 2px 16px 0 rgba(0,0,0,0.06)",
    cardHover: "0 4px 24px 0 rgba(0,0,0,0.08)",
    container: "0 2px 16px 0 rgba(47,40,118,0.04)",
  },
  borders: {
    primary: "1px solid #000088",
    secondary: "1.5px solid #E3E6F0",
    dashed: "2px dashed #CFCFCF",
    select: "1.5px solid #D5D9EB",
    button: "1px solid rgba(74,61,158,1)",
  },
};

function GetInsights({
  checkSkill,
  subSkills,
  chatGptButton,
  chatGPTData,
  interviewQuestionsLoading,
  advancedQuestionsLoading,
  intermediateQuestionsLoading,
  basicQuestionsLoading,
  interviewQuestions,
  disruptCareerData,
  Hide_google_trends,
  RetryGPT,
  addSkillRelevance,
  easeOfLearning,
  PotentialSalaryIncraese,
  isMobile,
  initialCount,
  randomIntFromInterval,
  setDisruptCareerLoading,
  disruptCareerLoading,
  advancedQuestions,
  setAdvancedQuestions,
  intermediateQuestions,
  setIntermediateQuestions,
  basicQuestions,
  setBasicQuestions,
  selectedTab,
  setSelectedTab,
  onQuestionSubmit,
  JobSuggestionData,
  setJobSuggestionData,
  questionFormLoading,
  questions,
  methodsJobSuggestion,
  formVisible,
  setFormVisible,
  topSkillsQuestions,
  topSkillsQuestionsLoading,
  topSkillsQuestionsError,
  topSkills = [],
  onFetchTopSkillsQuestions,
  resultId,
  skillName,
  skill,
  aiToolsData,
  aiToolsLoading,
  aiToolsError,
  disruptionData,
  disruptionLoading,
  disruptionError,
}) {
  const dispatch = useDispatch();

  // Add state for higher roles API data and loading
  const [higherRolesData, setHigherRolesData] = useState({
    suggestedRolesWithSalaryIncrease: [],
    otherRolesWithResponsibilities: [],
    higherRolesWithPromotions: {},
  });
  const [higherRolesLoading, setHigherRolesLoading] = useState(false);


  useEffect(() => {
    setHigherRolesData({
      suggestedRolesWithSalaryIncrease: [],
      otherRolesWithResponsibilities: [],
      higherRolesWithPromotions: {},
    });
    setHigherRolesLoading(true);
    apis
      .POST({
        endpoint: "/get-higher-roles",
        payload: { skillName: checkSkill },
        options: { type: "json" },
      })
      .then((res) => {
        setHigherRolesData({
          suggestedRolesWithSalaryIncrease:
            res.data.higherRoles?.suggestedRolesWithSalaryIncrease || [],
          otherRolesWithResponsibilities:
            res.data.higherRoles?.otherRolesWithResponsibilities || [],
          higherRolesWithPromotions:
            res.data.higherRoles?.higherRolesWithPromotions || null,
        });
        setHigherRolesLoading(false);
      })
      .catch(() => setHigherRolesLoading(false));
  }, []);

  // --- Income Insights Section (Figma style, responsive) ---
  const [showCitySection, setShowCitySection] = useState(false);
  const userState = useSelector((state) => state.UserLocationReducer.userState);
  const [selectedState, setSelectedState] = useState(userState);
  const initialCountry = {
    name: "India",
    shortName: "IN",
  };
  const userCountry = useSelector(
    (state) => state.UserLocationReducer.userCountry
  );
  const [selectedCountry, setSelectedCountry] = useState(userCountry);

  const [selectedExperience, setSelectedExperience] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const userCurrentCity = useSelector(
    (state) => state.UserLocationReducer.userCity
  );
  const countries = useSelector(
    (state) => state.UserLocationReducer.countriesList
  );
  const userSalaryRange =
    useSelector((state) => state.UserLocationReducer.userSalaryRange) || [];
  const userExperienceRange =
    useSelector((state) => state.UserLocationReducer.userExperienceRange) || [];
  const newExperienceRange = userExperienceRange.filter(
    (exp) =>
      !userSalaryRange.some((salary) => salary.experienceRange === exp.label)
  );
  const rangeSkeleton = useSelector(
    (state) => state.UserLocationReducer.rangeSkeleton
  );
  const topCities = useSelector(
    (state) => state.UserLocationReducer.userTopCities
  );
  const singleCityLoading = useSelector(
    (state) => state.UserLocationReducer.singleCityLoading
  );
  const citiesRangeSkeleton = useSelector(
    (state) => state.UserLocationReducer.citiesRangeSkeleton
  );
  const singleExperienceSkeleton = useSelector(
    (state) => state.UserLocationReducer.singleExperienceSkeleton
  );
  const userStateList = useSelector(
    (state) => state.UserLocationReducer.userStateList
  );
  const salaryRangeByCities = useSelector(
    (state) => state.UserLocationReducer.salaryRangeByCities
  );
  const citiesList = useSelector(
    (state) => state.UserLocationReducer.citiesList
  );
  const newCitiesList = citiesList?.filter(
    (city) => !salaryRangeByCities?.some((city2) => city2.city === city.name)
  );
  const mappedCities = newCitiesList?.map((item) => ({
    ...item,
    countryShortName: userCountry.shortName,
  }));

  const salaryRangeSkeletonBox = useSelector(
    (state) => state.UserLocationReducer.salaryRangeSkeletonBox
  );

  // User authentication data for payment
  const userName = useSelector((state) => state.AuthReducer?.user?.name || "");
  const userEmail = useSelector((state) => state.AuthReducer?.userEmail || "");
  const resultIdFromRedux = useSelector(
    (state) => state?.ResultReducer?.resultId || null
  );
  // Use prop if provided, otherwise use Redux value
  const finalResultId = resultId || resultIdFromRedux;

  const Razor_Pay_key = useFeatureFlag("Razor_Pay_key");
  const New_Theme = useFeatureFlag("New_Theme");
  const subscriptionPriceFlag = useFeatureFlag(
    "Techrelevance_TopSkill_Interview_Question_Price"
  );
  const subscriptionAmount = Number(subscriptionPriceFlag || 0);
  const isPaidSubscription = subscriptionAmount > 0;

  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionChecking, setSubscriptionChecking] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const [clickedExperience, setClickedExperience] = useState("fresher");

  const cityOptions = [
    "Select City",
    ...(Array.isArray(mappedCities)
      ? mappedCities.map((item) => item.name)
      : []),
  ];

  useEffect(() => {
    if (!isPaidSubscription) {
      setHasSubscription(true);
      setSubscriptionChecking(false);
      return;
    }

    let isMounted = true;

    const verifySubscription = async () => {
      try {
        setSubscriptionChecking(true);
        const response = await apis.GET({
          endpoint: "/check-user-subscription",
          options: { type: "json" },
        });
        if (!isMounted) return;
        const active = !!response?.data?.hasActiveSubscription;
        setHasSubscription(active);
      } catch (error) {
        if (!isMounted) return;
        console.error("Error verifying subscription:", error);
        setHasSubscription(false);
      } finally {
        if (isMounted) {
          setSubscriptionChecking(false);
        }
      }
    };

    verifySubscription();

    return () => {
      isMounted = false;
    };
  }, [isPaidSubscription]);

  // Restore body overflow when payment modal closes
  useEffect(() => {
    if (!paymentModalOpen) {
      // Use setTimeout to ensure Material-UI has finished its cleanup
      const timer = setTimeout(() => {
        document.body.style.overflow = "";
        document.body.style.height = "";
        document.body.style.paddingRight = "";
        // Also check documentElement
        document.documentElement.style.overflow = "";
        document.documentElement.style.height = "";
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [paymentModalOpen]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Restore body scroll when component unmounts
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
    };
  }, []);

  const handleSubscriptionSuccess = useCallback(async () => {
    setHasSubscription(true);
    setPaymentLoading(false);
    setPaymentModalOpen(false);
    // Explicitly restore body overflow when modal closes
    setTimeout(() => {
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
    }, 100);

    if (typeof onFetchTopSkillsQuestions === "function" && topSkills?.length) {
      try {
        await onFetchTopSkillsQuestions(topSkills);
      } catch (error) {
        console.error(
          "Failed to fetch top skills interview questions post-subscription:",
          error
        );
      }
    }
  }, [onFetchTopSkillsQuestions, topSkills]);

  const handleSubscriptionFailure = useCallback((error) => {
    console.error("Payment or subscription save failed:", error);
    setPaymentLoading(false);
    setPaymentModalOpen(false);
    // Explicitly restore body overflow when modal closes
    setTimeout(() => {
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
    }, 100);
  }, []);

  const { initiatePayment: startSubscriptionPayment } = usePaymentModule({
    amount: subscriptionAmount,
    userName,
    userEmail,
    razorpayKey: Razor_Pay_key,
    themeColor: New_Theme ? "#21175E" : "#6652E4",
    resultId,
    onSuccess: handleSubscriptionSuccess,
    onFailure: handleSubscriptionFailure,
    onModalOpen: () => {
      setPaymentLoading(true);
      setPaymentModalOpen(true);
    },
    onModalClose: () => {
      setPaymentLoading(false);
      setPaymentModalOpen(false);
    },
  });

  const handleSubscriptionPayment = useCallback(() => {
    if (!isPaidSubscription) {
      return;
    }
    startSubscriptionPayment();
  }, [isPaidSubscription, startSubscriptionPayment]);

  const subscriptionButtonLabel = paymentLoading
    ? "Processing..."
    : isPaidSubscription
      ? `Subscribe ₹${subscriptionAmount}/month`
      : "Subscribe";

  const renderSubscriptionCTA = (title) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(8px)",
        borderRadius: "24px",
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          p: { xs: 3, sm: 4, md: 5 },
          maxWidth: "500px",
          maxHeight: "700px",
        }}
      >
        <Box
          sx={{
            width: { xs: "80px", sm: "100px" },
            height: { xs: "80px", sm: "100px" },
            borderRadius: "50%",
            backgroundColor: "rgba(102,82,228,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
            mb: 3,
          }}
        >
          <LockIcon
            sx={{
              fontSize: { xs: "40px", sm: "50px" },
              color: "#6652E4",
            }}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "20px", sm: "24px", md: "28px" },
            fontWeight: 700,
            color: "#262655",
            mb: 2,
          }}
        >
          Unlock {title}
        </Typography>

        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "14px", sm: "15px", md: "16px" },
            fontWeight: 400,
            color: "#666",
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          Get monthly access to premium insights tailored for your career
          growth. Subscribe now to explore exclusive interview guidance and
          game-changing opportunities.
        </Typography>

        <Button
          variant="contained"
          onClick={handleSubscriptionPayment}
          disabled={paymentLoading}
          startIcon={
            paymentLoading ? <CircularProgress size={20} /> : <LockIcon />
          }
          sx={{
            backgroundColor: "#6652E4",
            color: "#fff",
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "14px", sm: "16px" },
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "12px",
            px: { xs: 3, sm: 4 },
            py: { xs: 1.5, sm: 2 },
            boxShadow: "0px 4px 12px rgba(102,82,228,0.3)",
            "&:hover": {
              backgroundColor: "#5542d4",
              boxShadow: "0px 6px 16px rgba(102,82,228,0.4)",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
        >
          {subscriptionButtonLabel}
        </Button>
      </Box>
    </Box>
  );

  const interviewQuestionCount =
    typeof interviewQuestions === "object" && interviewQuestions !== null
      ? Object.keys(interviewQuestions).length
      : 0;
  const hasGameChangerContent = Array.isArray(disruptCareerData)
    ? disruptCareerData.length > 0
    : !!disruptCareerData;
  const shouldShowInterviewSection =
    interviewQuestionCount > 0 || (isPaidSubscription && !hasSubscription);
  const shouldShowGameChangerSection =
    hasGameChangerContent || (isPaidSubscription && !hasSubscription);

  useEffect(() => {
    setSelectedState(userState);
  }, [userState]);

  useEffect(() => {
    const hasMatchingCountry = topCities?.some(
      (city) => city.countryShortName === userCountry.shortName
    );

    if (!hasMatchingCountry) {
      fetchTopCiteData(userCountry, userState); // assuming userState = capitalState
    }
  }, []);

  // Set initial selectedCountry from Redux store
  useEffect(() => {
    dispatch(setUserCountry(selectedCountry));
  }, [dispatch, selectedCountry]);

  // Set initial selectedState from Redux store
  useEffect(() => {
    // fetchTopCiteData(userCountry, userState);
  }, []);

  const fetchSalaryByExperience = async (experience) => {
    try {
      dispatch(setSingleSkeleton(true));
      const result = await fetchSalaryRange(
        userCountry,
        userState,
        userCurrentCity,
        checkSkill,
        experience
      );
      const newResults = [...userSalaryRange, result];
      dispatch(setSalaryRange(newResults));
      dispatch(setSingleSkeleton(false));
      setSelectedExperience("Select Experience");
    } catch (error) {
      console.log(error);
      dispatch(setSingleSkeleton(false));
    }
  };

  const fetchSalaryByCity = async (city, experience) => {
    try {
      dispatch(setSingleCitySkeleton(true));
      const result = await fetchSalaryRange(
        userCountry,
        userState,
        city,
        checkSkill,
        experience
      );
      const newResults = [...salaryRangeByCities, result];
      dispatch(setSalaryRangeByCities(newResults));
      dispatch(setSingleCitySkeleton(false));
      setSelectedCity("Select City");
    } catch (error) {
      console.error("Failed to fetch countries:", error);
      dispatch(setSingleCitySkeleton(false));
    }
  };

  // For Autocomplete, value is the selected option directly
  const handleChangeExperience = (exp) => {
    if (exp && exp !== "Select Experience") {
      setSelectedExperience(exp);
      fetchSalaryByExperience(exp);
    }
  };

  const handleChangeCountry = (country) => {
    if (!country) return;
    dispatch(setSalaryRangeSkeleton(true));
    dispatch(setCitiesRangeSkeleton(true));
    setSelectedCountry(country);
    const experience =
      clickedExperience.years !== undefined
        ? clickedExperience.years
        : "fresher";
    fetchLocationData(
      country,
      dispatch,
      checkSkill,
      userExperienceRange,
      countries,
      experience
    );
  };


  const fetchTopCiteData = async (userCountry, userState) => {
    try {
      const { cities } = await fetchTopCities(userCountry, userState);
      const years =
        clickedExperience.years !== undefined
          ? clickedExperience.years
          : "fresher";
      dispatch(setUserTopCities(cities));
      dispatch(setUserState(userState));
      const { states } = await fetchStates(userCountry);
      dispatch(setUserStateList(states));
      fetchSalaryRangeByCities(
        userCountry,
        userState,
        cities,
        checkSkill,
        years,
        dispatch
      );
    } catch (error) {
      console.error("Failed to fetch top cities:", error);
    }
  };

  const handleChangeState = (state) => {
    if (!state) return;
    dispatch(setCitiesRangeSkeleton(true));
    fetchCitiesInstance(state, dispatch);
    setSelectedState(state);
    dispatch(setUserState(state));
    fetchTopCiteData(userCountry, state);
  };

  const handleChangeCity = (city) => {
    if (!city || city === "Select City") return;
    setSelectedCity(city);
    const selected = mappedCities.find((item) => item.name === city);
    const selectedExp = clickedExperience.years;
    if (selected) {
      fetchSalaryByCity(selected, selectedExp);
    }
  };

  // Static dropdown options for experience and city
  const experienceOptions = [
    "Select Experience",
    ...newExperienceRange.map((exp) => exp.label),
  ];

  const experienceData = userSalaryRange.map((item) => ({
    ...item,
    years: item.experienceRange,
    salaries: "N/A",
    formattedAverageSalary: item.formattedAverageSalary,
    formattedRange: item.formattedRange,
  }));

  const handleCardClick = async (item) => {
    dispatch(setCitiesRangeSkeleton(true));
    setClickedExperience(item);
    const experienceRange = item.years;
    setShowCitySection(true);

    if (!citiesList || citiesList.length === 0) {
      await fetchCitiesInstance(userState, dispatch);
      dispatch(setCitiesRangeSkeleton(false));
    }

    if (topCities === undefined) {
      await fetchTopCiteData(userCountry, userState);
    } else {
      await fetchSalaryRangeByCities(
        userCountry,
        userState,
        topCities,
        checkSkill,
        experienceRange,
        dispatch
      );
    }
  };

  const renderSwiperCard = (item, type) => {
    const isSelected =
      type === "experience" && clickedExperience?.years === item.years;

    return (
      <Box
        sx={{
          width: { xs: "150px" },
          minHeight: "135px",
          backgroundColor: isSelected
            ? STYLES.colors.selected
            : STYLES.colors.backgroundWhite,
          border: isSelected
            ? `2px solid ${STYLES.colors.accent}`
            : "1.5px solid #DDDDDD",
          borderRadius: STYLES.borderRadius.swiperCard,
          padding: "0px 16px",
          cursor: type === "experience" ? "pointer" : "default",
          textAlign: "left",
          boxShadow: STYLES.boxShadow.card,
          transition: "box-shadow 0.2s, background-color 0.2s, border 0.2s",
          "&:hover": {
            boxShadow:
              type === "experience" ? STYLES.boxShadow.cardHover : undefined,
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
        onClick={
          type === "experience"
            ? () => handleCardClick(item)
            : () => {
              console.log("clicked");
            }
        }
      >
        <Typography
          fontSize={16}
          fontWeight={700}
          color={STYLES.colors.secondary}
          sx={{ mb: type === "experience" ? "25px" : "5px" }}
        >
          {item?.years || item?.city}
        </Typography>
        {type === "city" && (
          <Typography
            fontSize={STYLES.typography.caption.fontSize}
            fontWeight={STYLES.typography.caption.fontWeight}
            color={STYLES.colors.textSecondary}
            mb={1}
          >
            {`(${item?.experienceRange})`}
          </Typography>
        )}
        <Typography
          fontSize={STYLES.typography.body.fontSize}
          fontWeight={600}
          color={STYLES.colors.textPrimary}
          mb={0.5}
        >
          {item?.formattedAverageSalary}
          {`${item.country !== "India" ? "/yr" : ""}`}
        </Typography>
        <Typography
          fontSize={STYLES.typography.caption.fontSize}
          fontWeight={STYLES.typography.caption.fontWeight}
          color={STYLES.colors.textTertiary}
        >
          {item?.formattedRange}
        </Typography>
      </Box>
    );
  };

  const renderSwiperSection = (
    title,
    list,
    type,
    tagLeft,
    tagRight,
    dropdownText,
    extraSx = {},
    singleLoading,
    rangeLoading,
    iconName = null
  ) => {
    const getSkeletonCount = () => {
      const width = window.innerWidth;
      if (width <= 480) return 1;
      if (width <= 768) return 1;
      if (width <= 1024) return 1;
      if (width <= 1600) return 1;
      return 1;
    };

    return (
      <Box
        className="annual-income-experience"
        sx={{
          background: STYLES.colors.backgroundWhite,
          padding: STYLES.spacing,
          borderRadius: STYLES.borderRadius.card,
          boxShadow: STYLES.boxShadow.card,
          border: STYLES.borders.secondary,
          ...extraSx,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: "10px",
          }}
        >
          {iconName && renderCustomIcon(iconName, 40)}
          <Typography
            variant="h6"
            sx={{
              fontFamily: STYLES.typography.fontFamily,
              fontWeight: STYLES.typography.title.fontWeight,
              color: STYLES.colors.primary,
              textAlign: "center",
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", md: "flex-end" },
            mb: 2,
          }}
        >
          <Autocomplete
            options={countries}
            value={selectedCountry}
            onChange={(_, country) => handleChangeCountry(country)}
            getOptionLabel={(c) => c.name}
            isOptionEqualToValue={(a, b) => a.name === b.name}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Country"
                sx={{
                  "& .MuiInputBase-input": {
                    fontWeight: 600,
                    color: STYLES.colors.accent,
                    fontSize: STYLES.typography.body.fontSize,
                  },
                  width: "200px",
                  "& .MuiSvgIcon-root": { color: STYLES.colors.accent },
                }}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Box>
        <Grid
          container
          alignItems="center"
          sx={{
            display: "flex",
            justifyContent: {
              xs: "space-around",
              sm: "space-around",
              md: "unset",
            },
          }}
        >
          <Grid item xs={12} sm={12}>
            <Slider {...settings}>
              {list?.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex !important",
                    justifyContent: "center",
                  }}
                >
                  {/* {console.log(item, "item")} */}

                  {rangeLoading ? (
                    <Box
                      sx={{
                        width: { xs: "180px" },
                        minHeight: "135px",
                        borderRadius: STYLES.borderRadius.swiperCard,
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{
                          bgcolor: STYLES.colors.skeleton,
                          borderRadius: STYLES.borderRadius.swiperCard,
                          "& .MuiSkeleton-wave": {
                            background: STYLES.colors.skeletonWave,
                          },
                        }}
                        animation="wave"
                      />
                    </Box>
                  ) : (
                    renderSwiperCard(item, type)
                  )}
                </Box>
              ))}
              {singleLoading ? (
                <Box
                  sx={{
                    display: "flex !important",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "180px" },
                      minHeight: "135px",
                      borderRadius: STYLES.borderRadius.swiperCard,
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="100%"
                      sx={{
                        bgcolor: STYLES.colors.skeleton,
                        borderRadius: STYLES.borderRadius.swiperCard,
                        "& .MuiSkeleton-wave": {
                          background: STYLES.colors.skeletonWave,
                        },
                      }}
                      animation="wave"
                    />
                  </Box>
                </Box>
              ) : (
                ""
              )}
              {singleLoading && type === "city" ? (
                <Box
                  sx={{
                    width: { xs: "180px" },
                    minHeight: "135px",
                    borderRadius: STYLES.borderRadius.swiperCard,
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{
                      bgcolor: STYLES.colors.skeleton,
                      borderRadius: STYLES.borderRadius.swiperCard,
                      "& .MuiSkeleton-wave": {
                        background: STYLES.colors.skeletonWave,
                      },
                    }}
                    animation="wave"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex !important",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "180px" },
                      minHeight: "135px",
                      border: STYLES.borders.dashed,
                      borderRadius: STYLES.borderRadius.swiperCard,
                      backgroundColor: STYLES.colors.backgroundHover,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AddCircleOutline
                      sx={{ fontSize: 50, color: STYLES.colors.icon }}
                    />
                    <Box sx={{ mt: 2, width: "80%" }}>
                      <Autocomplete
                        options={experienceOptions}
                        value={selectedExperience}
                        onChange={(_, exp) => handleChangeExperience(exp)}
                        getOptionLabel={(exp) => exp}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Experience"
                            sx={{
                              fontSize: STYLES.typography.body.fontSize,
                              fontWeight: STYLES.typography.body.fontWeight,
                              color: STYLES.colors.secondary,
                              "& .MuiInputBase-input": {
                                color: STYLES.colors.accent,
                                fontSize: STYLES.typography.body.fontSize,
                              },
                              width: "100%",
                              "& .MuiSvgIcon-root": {
                                color: STYLES.colors.secondary,
                              },
                            }}
                            variant="outlined"
                            size="small"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Slider>
          </Grid>
        </Grid>
        <Typography
          variant="body2"
          sx={{
            color: STYLES.colors.textTertiary,
            mt: 1,
            mb: 2,
            textAlign: "center",
            fontFamily: STYLES.typography.fontFamily,
            fontSize: STYLES.typography.caption.fontSize,
            fontWeight: STYLES.typography.caption.fontWeight,
          }}
          id="country-modal-description"
        >
          {`Salary ranges are AI-generated estimates based on ${type === "experience"
            ? "experience and country"
            : "country, state and city"
            }. Actual salaries may vary.`}
        </Typography>
        <TechWorthRedirectAds />
      </Box>
    );
  };
  const [skeletonItems, setSkeletonItems] = useState([]);

  useEffect(() => {
    const getSkeletonCount = () => {
      const width = window.innerWidth;
      if (width <= 480) return 1;
      if (width <= 768) return 2.5;
      if (width <= 1024) return 4.5;
      if (width <= 1600) return 5.5;
      if (width >= 1600) return 6.5;
      return 1;
    };
    setSkeletonItems(Array.from({ length: getSkeletonCount() }));
  }, [skeletonItems]);

  const renderSwiperSectionCity = (
    title,
    list,
    type,
    tagLeft,
    tagRight,
    dropdownText,
    extraSx = {},
    singleLoading,
    rangeLoading
  ) => {
    return (
      <Box
        className="annual-income-city"
        sx={{
          background: STYLES.colors.backgroundWhite,
          padding: STYLES.spacing,
          borderRadius: STYLES.borderRadius.card,
          boxShadow: STYLES.boxShadow.card,
          border: STYLES.borders.secondary,
          ...extraSx,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: STYLES.typography.fontFamily,
            fontWeight: STYLES.typography.title.fontWeight,
            color: STYLES.colors.primary,
            textAlign: "center",
            mb: "10px",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", md: "flex-end" },
            mb: 2,
          }}
        >
          {rangeLoading ? (
            <DropdownSkeleton />
          ) : (
            <Autocomplete
              options={userStateList}
              value={selectedState}
              onChange={(_, state) => handleChangeState(state)}
              getOptionLabel={(s) => s.name}
              isOptionEqualToValue={(a, b) => a.name === b.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select State"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontWeight: 600,
                      color: STYLES.colors.accent,
                      fontSize: STYLES.typography.body.fontSize,
                    },
                    width: "200px",
                    "& .MuiSvgIcon-root": { color: STYLES.colors.accent },
                  }}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          )}
        </Box>
        <Grid
          container
          alignItems="center"
          sx={{
            display: "flex",
            justifyContent: {
              xs: "space-around",
              sm: "space-around",
              md: "unset",
            },
          }}
        >
          <Grid item xs={12} sm={12}>
            {rangeLoading ? (
              <Slider {...settings}>
                {skeletonItems.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex !important",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "180px" },
                        minHeight: "135px",
                        borderRadius: "16px",
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{
                          bgcolor: "#E3E6F0",
                          borderRadius: "16px",
                          "& .MuiSkeleton-wave": {
                            background: `linear-gradient(90deg, #E3E6F0 40%, #F9FBFF 60%, #E3E6F0 80%)`,
                          },
                        }}
                        animation="wave"
                      />
                    </Box>
                  </Box>
                ))}
              </Slider>
            ) : (
              <Slider {...settings}>
                {list?.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex !important",
                      justifyContent: "center",
                    }}
                  >
                    {renderSwiperCard(item, type)}
                  </Box>
                ))}
                {singleLoading && (
                  <Box
                    sx={{
                      display: "flex !important",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "180px" },
                        minHeight: "135px",
                        borderRadius: "16px",
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{
                          bgcolor: "#E3E6F0",
                          borderRadius: "16px",
                        }}
                        animation="wave"
                      />
                    </Box>
                  </Box>
                )}
                {!singleLoading && (
                  <Box
                    sx={{
                      display: "flex !important",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "180px" },
                        minHeight: "135px",
                        border: "2px dashed #CFCFCF",
                        borderRadius: "16px",
                        backgroundColor: "#F9FBFF",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AddCircleOutline
                        sx={{ fontSize: 50, color: "#B0B0B0" }}
                      />
                      <Box sx={{ mt: 2, width: "80%" }}>
                        <Box sx={{ position: "relative", width: "100%" }}>
                          <Autocomplete
                            options={cityOptions}
                            value={selectedCity}
                            onChange={(_, city) => handleChangeCity(city)}
                            getOptionLabel={(c) => c}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select City"
                                sx={{
                                  fontSize: 15,
                                  fontWeight: 500,
                                  color: "#232655",
                                  "& .MuiInputBase-input": {
                                    color: STYLES.colors.accent,
                                    fontSize: STYLES.typography.body.fontSize,
                                  },
                                  width: "100%",
                                  visibility: singleCityLoading
                                    ? "hidden"
                                    : "visible",
                                  "& .MuiSvgIcon-root": { color: "#232655" },
                                }}
                                variant="outlined"
                                size="small"
                              />
                            )}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Slider>
            )}
          </Grid>
        </Grid>
        <Typography
          variant="body2"
          sx={{
            color: STYLES.colors.textTertiary,
            mt: 1,
            mb: 2,
            textAlign: "center",
            fontFamily: STYLES.typography.fontFamily,
            fontSize: STYLES.typography.caption.fontSize,
            fontWeight: STYLES.typography.caption.fontWeight,
          }}
          id="country-modal-description"
        >
          {`Salary ranges are AI-generated estimates based on ${type === "experience"
            ? "experience and country"
            : "country, state and city"
            }. Actual salaries may vary.`}
        </Typography>
      </Box>
    );
  };

  const settings = {
    dots: false,
    speed: 500,
    infinite: false,
    slidesToShow: 6.5,
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      {
        breakpoint: 1600,
        settings: {
          centerPadding: "150px",
          slidesToShow: 5.5,
        },
      },
      {
        breakpoint: 1440,
        settings: {
          centerPadding: "150px",
          slidesToShow: 5.5,
        },
      },
      {
        breakpoint: 1400,
        settings: {
          centerPadding: "150px",
          slidesToShow: 4.5,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          centerPadding: "150px",
          slidesToShow: 3.5,
        },
      },
      {
        breakpoint: 768,
        settings: {
          centerPadding: "100px",
          slidesToShow: 2.5,
        },
      },
      {
        breakpoint: 480,
        settings: {
          centerPadding: "50px",
          slidesToShow: 1,
        },
      },
    ],
  };



  return (
    <Box
      component="div"
      sx={{
        position: "relative",
        backgroundImage: `url('/assets/images/tech-relevence-chatgpt-bg-02.svg')`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        p: { xs: "0px", sm: "20px", md: "46px 44px" },
        borderRadius: { xs: "20px", md: "66px" },
        overflow: "hidden",
        border: { sm: `2px solid rgba(102,82,228,0.25)` },
      }}
    >
      <Modal
        open={paymentModalOpen}
        onClose={() => {
          if (!paymentLoading) {
            setPaymentModalOpen(false);
            // Explicitly restore body overflow when modal closes
            setTimeout(() => {
              document.body.style.overflow = "";
              document.body.style.height = "";
              document.body.style.paddingRight = "";
              document.documentElement.style.overflow = "";
              document.documentElement.style.height = "";
            }, 100);
          }
        }}
        aria-labelledby="subscription-payment-modal"
        aria-describedby="subscription-processing"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
          position="fixed"
        >
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      </Modal>
      <Box id="overview" sx={{ display: "flex", alignItems: "center", mb: "43px" }}>
        <Box
          sx={{
            mr: { xs: "10px", sm: "10px", md: "20px" },
            width: { xs: "46px", md: "58px" },
            height: "100%",
          }}
        >
          <img
            src="/assets/images/bulb-img-01.svg"
            width="100%"
            height="100%"
            alt="bulb-img"
            loading="lazy"
          />
        </Box>
        <Typography
          sx={{
            fontFamily: STYLES.typography.fontFamily,
            fontSize: { xs: "24px", md: "32px" },
            fontWeight: 700,
            color: STYLES.colors.secondary,
          }}
        >
          {checkSkill}
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {/* Sub skills */}
        <Grid item xs={12} sm={6} lg={3} xl={3}>
          {subSkills.length !== 0 ? (
            <Box
              id="sub-skills"
              sx={{
                border: STYLES.borders.primary,
                borderRadius: "39px",
                background: STYLES.colors.background,
                height: "100%",
                boxSizing: "inherit",
              }}
              className="sub-skills"
              p={STYLES.spacing}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                {renderCustomIcon("sub-skills", 40)}
                <Typography
                  color="primary"
                  variant="h6"
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    textAlign: "center",
                    // fontSize: STYLES.typography.title.fontSize,
                    fontWeight: STYLES.typography.title.fontWeight,
                    color: STYLES.colors.primary,
                  }}
                >
                  Sub skills
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  mb: 2,
                  flexDirection: { xs: "column", md: "column" },
                  alignItems: { xs: "center", md: "center" },
                }}
              >
                <Box>
                  <SubSkills
                    subSkills={subSkills}
                    parentName={checkSkill}
                    bgColor={STYLES.colors.backgroundWhite}
                    avatarIcon=""
                    chatGptButton={chatGptButton}
                  />
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              id="sub-skills"
              sx={{
                border: STYLES.borders.primary,
                borderRadius: "39px",
                background: STYLES.colors.background,
                height: "100%",
                boxSizing: "inherit",
              }}
              p={STYLES.spacing}
              className="skill-popularity"
            >
              <Typography
                color="primary"
                variant="h6"
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  textAlign: "center",
                  // fontSize: STYLES.typography.title.fontSize,
                  fontWeight: STYLES.typography.title.fontWeight,
                  color: STYLES.colors.primary,
                }}
              >
                Popularity
              </Typography>
              {chatGptButton ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box pt={{ xs: 0, md: 0, lg: 6 }}>
                  <PopularityChart
                    value={parseInt(chatGPTData?.popularityRating) * 10 || 10}
                    color={STYLES.colors.accent}
                  />
                </Box>
              )}
            </Box>
          )}
        </Grid>
        {/* Enterprise Adoption */}
        <Grid item xs={12} sm={6} lg={3} xl={3}>
          <Box
            id="enterprise-adoption"
            sx={{
              border: STYLES.borders.primary,
              borderRadius: "39px",
              background: STYLES.colors.background,
              height: "100%",
              boxSizing: "inherit",
            }}
            className="enterprise-adoption"
            p={STYLES.spacing}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 1,
              }}
            >
              {renderCustomIcon("enterprice", 40)}
              <Typography
                color="primary"
                variant="h6"
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  textAlign: "center",
                  // fontSize: STYLES.typography.title.fontSize,
                  fontWeight: STYLES.typography.title.fontWeight,
                  color: STYLES.colors.primary,
                }}
              >
                Enterprise Adoption
              </Typography>
            </Box>
            {chatGptButton ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <IndustrialUsageChart
                  value={parseInt(chatGPTData?.percentageUsage || 10)}
                  color={STYLES.colors.accent}
                />
              </Box>
            )}
            {subSkills?.length !== 0 && (
              <>
                <Typography
                  color="primary"
                  variant="h6"
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    textAlign: "center",
                    // fontSize: STYLES.typography.title.fontSize,
                    fontWeight: STYLES.typography.title.fontWeight,
                    color: STYLES.colors.primary,
                  }}
                >
                  Popularity
                </Typography>
                {chatGptButton ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", my: 5 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box pt={{ xs: 0, md: 0, lg: 6 }}>
                    <PopularityChart
                      value={parseInt(chatGPTData?.popularityRating) * 10 || 10}
                      color={STYLES.colors.accent}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Grid>
        {/* Latest Technologies */}
        <Grid item xs={12} sm={12} lg={6} xl={6}>
          <Box
            id="learn-earn"
            sx={{
              border: STYLES.borders.primary,
              borderRadius: "39px",
              background: STYLES.colors.background,
              height: "100%",
              boxSizing: "inherit",
            }}
            className="lear-earn-recommendations"
            p={STYLES.spacing}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 1,
              }}
            >
              {renderCustomIcon("learn-earn", 40)}
              <Typography
                color="primary"
                variant="h6"
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  textAlign: "center",
                  // fontSize: STYLES.typography.title.fontSize,
                  fontWeight: STYLES.typography.title.fontWeight,
                  color: STYLES.colors.primary,
                }}
              >
                Latest Technologies
              </Typography>
            </Box>
            {chatGptButton ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                {chatGPTData?.latestTechnologies &&
                  Array.isArray(
                    chatGPTData?.latestTechnologies || chatGPTData
                  ) ? (
                  <TableContainer>
                    <Table
                      sx={{ minWidth: 200, overflowX: "hidden" }}
                      aria-label="simple table"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">
                            <Typography
                              sx={{
                                fontFamily: STYLES.typography.fontFamily,
                                fontSize:
                                  STYLES.typography.tableHeader.fontSize,
                                fontWeight:
                                  STYLES.typography.tableHeader.fontWeight,
                                textAlign: "center",
                              }}
                            >
                              Skill
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              sx={{
                                fontFamily: STYLES.typography.fontFamily,
                                fontSize:
                                  STYLES.typography.tableHeader.fontSize,
                                fontWeight:
                                  STYLES.typography.tableHeader.fontWeight,
                                textAlign: "center",
                              }}
                            >
                              Ease of Learning
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              sx={{
                                fontFamily: STYLES.typography.fontFamily,
                                fontSize:
                                  STYLES.typography.tableHeader.fontSize,
                                fontWeight:
                                  STYLES.typography.tableHeader.fontWeight,
                                textAlign: "center",
                              }}
                            >
                              Potential Salary Increase
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chatGPTData?.latestTechnologies?.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell
                              component="th"
                              scope="row"
                              sx={{ display: "flex", border: "none" }}
                            >
                              <Box
                                component="img"
                                sx={{
                                  maxWidth: { xs: "16px", lg: "25px" },
                                }}
                                loading="lazy"
                                src="../../assets/images/fire-icon.svg"
                                alt="fire"
                              />
                              <Typography
                                sx={{
                                  fontFamily: STYLES.typography.fontFamily,
                                  fontSize:
                                    STYLES.typography.tableCell.fontSize,
                                  fontWeight:
                                    STYLES.typography.tableCell.fontWeight,
                                  textAlign: "left",
                                  paddingLeft: "8px",
                                }}
                                component="a"
                                href="#"
                                onClick={() => addSkillRelevance(item)}
                              >
                                {item?.skillName}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ border: "none" }}>
                              <Typography
                                sx={{
                                  fontFamily: STYLES.typography.fontFamily,
                                  fontSize:
                                    STYLES.typography.tableCell.fontSize,
                                  fontWeight:
                                    STYLES.typography.tableCell.fontWeight,
                                  textAlign: "center",
                                }}
                              >
                                {item?.easeOfLearning ||
                                  (Array.isArray(easeOfLearning) && easeOfLearning.length > 0
                                    ? easeOfLearning[index % easeOfLearning.length]
                                    : "Medium")}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ border: "none" }}>
                              <Typography
                                sx={{
                                  fontFamily: STYLES.typography.fontFamily,
                                  fontSize:
                                    STYLES.typography.tableCell.fontSize,
                                  fontWeight:
                                    STYLES.typography.tableCell.fontWeight,
                                  textAlign: "center",
                                }}
                              >
                                {item?.potentialSalaryIncreasePercentage ||
                                  (Array.isArray(PotentialSalaryIncraese) && PotentialSalaryIncraese.length > 0
                                    ? PotentialSalaryIncraese[index % PotentialSalaryIncraese.length]
                                    : "15")}
                                %
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    display="flex"
                    justifyContent="center"
                    flexDirection="column"
                    textAlign="center"
                  >
                    <Box>
                      <img
                        src="../../assets/images/empty-list.svg"
                        alt="data not found"
                        loading="lazy"
                        style={{ maxWidth: `${isMobile ? "50%" : "200px"}` }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: STYLES.typography.fontFamily,
                        textAlign: "center",
                        fontSize: STYLES.typography.subtitle,
                      }}
                    >
                      Recommendations are not found
                    </Typography>
                    {initialCount <= 3 && (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ flexWrap: "wrap", px: 2, py: { xs: 1, lg: 0 } }}
                      >
                        <Typography pr={1}>Retry with </Typography>
                        <Button
                          sx={{
                            maxWidth: "200px",
                            width: "100%",
                            marginLeft: { sm: "15px" },
                            color: STYLES.colors.buttonHover,
                            alignItems: "center",
                            justifyContent: "space-evenly",
                            padding: "7px 12px",
                            borderRadius: STYLES.borderRadius.button,
                            border: STYLES.borders.button,
                            background:
                              "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1))",
                            transition: "all 0.3s linear",
                            "&:hover": {
                              border: STYLES.borders.button,
                              background: STYLES.colors.buttonHover,
                              color: "rgba(108,82,229,1)",
                            },
                            "&:active,&:focus": {
                              background: STYLES.colors.buttonHover,
                              padding: "4px 10px",
                              border: `2px solid ${STYLES.colors.accent}`,
                              color: `${STYLES.colors.accent} !important`,
                            },
                          }}
                          endIcon={<RocketLaunch sx={{ fontSize: "16px" }} />}
                          onClick={() => RetryGPT(checkSkill)}
                        >
                          Get insights
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Grid>

        {/* Skill & Role Disruption Section */}
        <SkillAndRoleDisruptionSection
          disruptionData={disruptionData}
          disruptionLoading={disruptionLoading}
          disruptionError={disruptionError}
          checkSkill={checkSkill}
        />

        {/* Survival Strategy & Up-Skilling Section */}
        <SurvivalStrategySection
          disruptionData={disruptionData}
          disruptionLoading={disruptionLoading}
          disruptionError={disruptionError}
          checkSkill={checkSkill}
        />

        {/* AI Tools Section */}
        <AiToolsSection
          aiToolsData={aiToolsData}
          aiToolsLoading={aiToolsLoading}
          aiToolsError={aiToolsError}
          checkSkill={checkSkill}
        />

        {/* Suggested Roles */}
        <Grid item xs={12} sm={12} lg={12} xl={12}>
          <Box id="suggested-roles">
            <RolesAndResponsibilities
              checkSkill={checkSkill}
              isMobile={isMobile}
              loading={higherRolesLoading}
              suggestedRoles={higherRolesData.suggestedRolesWithSalaryIncrease}
              otherRoles={higherRolesData.otherRolesWithResponsibilities}
              higherRolesPromotions={higherRolesData.higherRolesWithPromotions}
              skillName={skillName || checkSkill}
              resultId={finalResultId}
            />
          </Box>
        </Grid>

        {/* Annual Income Based on Years of Experience */}
        {userStateList &&
          countries &&
          experienceData.length > 0 &&
          salaryRangeSkeletonBox === false && (
            <Grid item xs={12}>
              <Box
                id="salary-insights"
                sx={{
                  background: STYLES.colors.background,
                  p: STYLES.spacing,
                  borderRadius: STYLES.borderRadius.card,
                  border: STYLES.borders.primary,
                  boxShadow: STYLES.boxShadow.container,
                }}
              >
                {/* {console.log(experienceData,"experienceData")} */}
                {renderSwiperSection(
                  `Annual Income Based on Years of Experience for ${checkSkill}`,
                  experienceData,
                  "experience",
                  "Years of experience",
                  "Avg. Annual Salary",
                  "Select Experience",
                  {},
                  singleExperienceSkeleton,
                  rangeSkeleton,
                  "salary-insights"
                )}
                {showCitySection &&
                  renderSwiperSectionCity(
                    "Annual Income by City/State",
                    salaryRangeByCities,
                    "city",
                    "City / Year of Exp.",
                    "Avg. Annual Salary",
                    "Select City",
                    { mt: { xs: 3, sm: 4 } },
                    singleCityLoading,
                    citiesRangeSkeleton
                  )}
              </Box>
            </Grid>
          )}
        {IncomeByExperienceSkeleton === true && <IncomeByExperienceSkeleton />}

        {/* Annual income by role - Hidden */}
        {/* {skill?.skillType === "tech" || skill?.skillType === "management" ? (
          <Grid item xs={12}>
            <Box id="job-suggestions">
              <JobSuggestionComponent
                JobSuggestionData={JobSuggestionData}
                setJobSuggestionData={setJobSuggestionData}
                methodsJobSuggestion={methodsJobSuggestion}
                onQuestionSubmit={onQuestionSubmit}
                isMobile={isMobile}
                formVisible={formVisible}
                setFormVisible={setFormVisible}
                questionFormLoading={questionFormLoading}
                questions={questions}
              />
            </Box>
          </Grid>
        ) : ''} */}

        {/* Top Interview Questions */}
        {shouldShowInterviewSection && (
          <Grid
            item
            xs={12}
            sm={12}
            lg={hasGameChangerContent ? 6 : 12}
            xl={hasGameChangerContent ? 6 : 12}
          >
            <Box
              id="interview-questions"
              sx={{
                border: STYLES.borders.primary,
                borderRadius: "39px",
                background: STYLES.colors.background,
                height: "100%",
                boxSizing: "inherit",
              }}
              className="interview-questions"
              p={STYLES.spacing}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                {renderCustomIcon("interview-questions", 40)}
                <Typography
                  color="primary"
                  variant="h6"
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    textAlign: "center",
                    // fontSize: STYLES.typography.title.fontSize,
                    fontWeight: STYLES.typography.title.fontWeight,
                    color: STYLES.colors.primary,
                  }}
                >
                  Top Interview Questions for{" "}
                  <span
                    style={{ fontWeight: 700, color: STYLES.colors.secondary }}
                  >
                    {checkSkill}
                  </span>
                </Typography>
              </Box>
              {subscriptionChecking ? (
                <Box sx={{ mt: 2 }}>
                  <Skeleton
                    variant="rounded"
                    height={56}
                    sx={{ mb: 2, borderRadius: "12px" }}
                  />
                  {[1, 2, 3].map((item) => (
                    <Skeleton
                      key={item}
                      variant="rounded"
                      height={140}
                      sx={{ mb: 2, borderRadius: "20px" }}
                    />
                  ))}
                </Box>
              ) : hasSubscription || !isPaidSubscription ? (
                <InterviewQuestionByCategory
                  questions={interviewQuestions}
                  advancedQuestions={advancedQuestions}
                  setAdvancedQuestions={setAdvancedQuestions}
                  intermediateQuestions={intermediateQuestions}
                  setIntermediateQuestions={setIntermediateQuestions}
                  basicQuestions={basicQuestions}
                  setBasicQuestions={setBasicQuestions}
                  advancedQuestionsLoading={advancedQuestionsLoading}
                  intermediateQuestionsLoading={intermediateQuestionsLoading}
                  basicQuestionsLoading={basicQuestionsLoading}
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                />
              ) : (
                renderSubscriptionCTA("Top Interview Questions")
              )}
            </Box>
          </Grid>
        )}
        {shouldShowGameChangerSection && (
          <Grid
            item
            xs={12}
            sm={12}
            lg={interviewQuestionCount > 0 ? 6 : 12}
            xl={interviewQuestionCount > 0 ? 6 : 12}
          >
            <Box
              id="career-disruptors"
              sx={{
                border: STYLES.borders.primary,
                borderRadius: "39px",
                background: STYLES.colors.background,
                height: "100%",
                boxSizing: "inherit",
              }}
              className="disrupt-career-box"
              p={STYLES.spacing}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  p: STYLES.spacing,
                }}
              >
                {renderCustomIcon("game-changer", 40)}
                <Typography
                  color="primary"
                  variant="h6"
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    textAlign: "center",
                    // fontSize: STYLES.typography.title.fontSize,
                    fontWeight: STYLES.typography.title.fontWeight,
                    color: STYLES.colors.primary,
                  }}
                >
                  Game-Changers That Could Redefine Your Career and Key Skills
                </Typography>
              </Box>
              {subscriptionChecking ? (
                <Box sx={{ mt: 2 }}>
                  {[1, 2].map((item) => (
                    <Skeleton
                      key={item}
                      variant="rounded"
                      height={180}
                      sx={{ mb: 2, borderRadius: "20px" }}
                    />
                  ))}
                </Box>
              ) : hasSubscription || !isPaidSubscription ? (
                <DynamicContentRenderer
                  content={disruptCareerData}
                  disruptCareerLoading={disruptCareerLoading}
                />
              ) : (
                renderSubscriptionCTA("Game-Changer Insights")
              )}
            </Box>
          </Grid>
        )}

        {/* Top Skills Interview Questions */}
        {topSkills && topSkills.length > 1 && !topSkillsQuestionsError ? (
          <Grid item xs={12} sm={12} lg={12} xl={12}>
            <Box
              id="top-skills-interview-questions"
              sx={{
                border: STYLES.borders.primary,
                borderRadius: "39px",
                background: STYLES.colors.background,
                height: "100%",
                boxSizing: "inherit",
              }}
              className="top-skills-interview-questions"
              p={STYLES.spacing}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                {renderCustomIcon("interview-questions", 40)}
                <Typography
                  color="primary"
                  variant="h6"
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    textAlign: "center",
                    fontWeight: STYLES.typography.title.fontWeight,
                    color: STYLES.colors.primary,
                  }}
                >
                  Top Skills Interview Questions
                </Typography>
              </Box>
              <TopSkillsInterviewQuestions
                topSkillsQuestions={topSkillsQuestions}
                loading={topSkillsQuestionsLoading}
                error={topSkillsQuestionsError}
                topSkills={topSkills}
                amount={99}
                userName={userName}
                userEmail={userEmail}
                onFetchQuestions={onFetchTopSkillsQuestions}
                isSubscribed={hasSubscription}
                onSubscriptionActivated={handleSubscriptionSuccess}
                onInitiateSubscription={handleSubscriptionPayment}
              />
            </Box>
          </Grid>
        ) : null}

      </Grid>
      {Hide_google_trends === false && (
        <Box
          id="google-trends"
          sx={{
            border: STYLES.borders.primary,
            borderRadius: "39px",
            background: STYLES.colors.background,
            height: "100%",
            boxSizing: "inherit",
            p: STYLES.spacing,
            mt: 3,
          }}
        >
          <GoogleTrendsEmbed keyword={checkSkill} geo="" time="today 5-y" />
        </Box>
      )}
    </Box>
  );
}

export default GetInsights;
